/**
 * Gesetzliche Feiertage in Deutschland.
 *
 * Bewusst ohne externen Dienst: die Regeln sind fest, die Berechnung kostet
 * nichts, und ein Kalender darf nicht davon abhaengen, ob eine fremde API
 * gerade erreichbar ist.
 *
 * Die beweglichen Feiertage haengen alle am Ostersonntag; dafuer wird die
 * anonyme gregorianische Osterformel (Meeus/Jones/Butcher) benutzt.
 */
import { addDays } from '@/lib/datetime';
import type { Bundesland, LocalDate } from '@/domain/types';

export type { Bundesland };

export const BUNDESLAENDER: { code: Bundesland; name: string }[] = [
  { code: 'BW', name: 'Baden-Württemberg' },
  { code: 'BY', name: 'Bayern' },
  { code: 'BE', name: 'Berlin' },
  { code: 'BB', name: 'Brandenburg' },
  { code: 'HB', name: 'Bremen' },
  { code: 'HH', name: 'Hamburg' },
  { code: 'HE', name: 'Hessen' },
  { code: 'MV', name: 'Mecklenburg-Vorpommern' },
  { code: 'NI', name: 'Niedersachsen' },
  { code: 'NW', name: 'Nordrhein-Westfalen' },
  { code: 'RP', name: 'Rheinland-Pfalz' },
  { code: 'SL', name: 'Saarland' },
  { code: 'SN', name: 'Sachsen' },
  { code: 'ST', name: 'Sachsen-Anhalt' },
  { code: 'SH', name: 'Schleswig-Holstein' },
  { code: 'TH', name: 'Thüringen' },
];

/** Oles Bundesland (Roadmap 3, Entscheidung 2) - gilt, solange keins gewaehlt ist. */
export const STANDARD_BUNDESLAND: Bundesland = 'BB';

/** Gespeicherten (evtl. fehlenden oder kaputten) Wert pruefen. */
export function bundeslandAus(roh: unknown): Bundesland {
  return BUNDESLAENDER.some((b) => b.code === roh) ? (roh as Bundesland) : STANDARD_BUNDESLAND;
}

export const bundeslandName = (b: Bundesland): string => BUNDESLAENDER.find((x) => x.code === b)?.name ?? b;

export interface Holiday {
  date: LocalDate;
  name: string;
  /** `true`, wenn bundesweit gesetzlicher Feiertag. */
  bundesweit: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number): LocalDate => `${y}-${pad(m)}-${pad(d)}`;

/**
 * Ostersonntag nach der anonymen gregorianischen Osterformel.
 * Gueltig fuer die Jahre 1583 bis 4099 - fuer einen Lebenskalender reichlich.
 */
export function easterSunday(year: number): LocalDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(year, month, day);
}

/** Busz- und Bettag: der Mittwoch vor dem 23. November. */
function bussUndBettag(year: number): LocalDate {
  const nov23 = new Date(Date.UTC(year, 10, 23));
  // 3 = Mittwoch. Rueckwaerts bis zum vorigen Mittwoch.
  const offset = ((nov23.getUTCDay() - 3) + 7) % 7 || 7;
  return addDays(ymd(year, 11, 23), -offset);
}

export function holidaysForYear(year: number, land?: Bundesland): Holiday[] {
  const ostern = easterSunday(year);

  const list: (Holiday & { laender?: Bundesland[] })[] = [
    { date: ymd(year, 1, 1), name: 'Neujahr', bundesweit: true },
    { date: ymd(year, 1, 6), name: 'Heilige Drei Könige', bundesweit: false, laender: ['BW', 'BY', 'ST'] },
    { date: ymd(year, 3, 8), name: 'Internationaler Frauentag', bundesweit: false, laender: ['BE', 'MV'] },
    { date: addDays(ostern, -2), name: 'Karfreitag', bundesweit: true },
    { date: ostern, name: 'Ostersonntag', bundesweit: false, laender: ['BB', 'HE'] },
    { date: addDays(ostern, 1), name: 'Ostermontag', bundesweit: true },
    { date: ymd(year, 5, 1), name: 'Tag der Arbeit', bundesweit: true },
    { date: addDays(ostern, 39), name: 'Christi Himmelfahrt', bundesweit: true },
    { date: addDays(ostern, 49), name: 'Pfingstsonntag', bundesweit: false, laender: ['BB', 'HE'] },
    { date: addDays(ostern, 50), name: 'Pfingstmontag', bundesweit: true },
    { date: addDays(ostern, 60), name: 'Fronleichnam', bundesweit: false, laender: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL'] },
    { date: ymd(year, 8, 15), name: 'Mariä Himmelfahrt', bundesweit: false, laender: ['SL'] },
    { date: ymd(year, 10, 3), name: 'Tag der Deutschen Einheit', bundesweit: true },
    { date: ymd(year, 10, 31), name: 'Reformationstag', bundesweit: false, laender: ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'] },
    { date: ymd(year, 11, 1), name: 'Allerheiligen', bundesweit: false, laender: ['BW', 'BY', 'NW', 'RP', 'SL'] },
    { date: bussUndBettag(year), name: 'Buß- und Bettag', bundesweit: false, laender: ['SN'] },
    { date: ymd(year, 12, 25), name: '1. Weihnachtsfeiertag', bundesweit: true },
    { date: ymd(year, 12, 26), name: '2. Weihnachtsfeiertag', bundesweit: true },
  ];

  return list
    .filter((h) => h.bundesweit || (land ? h.laender?.includes(land) : false))
    .map(({ date, name, bundesweit }) => ({ date, name, bundesweit }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Nachschlagetabelle fuer einen Zeitraum - deckt Jahreswechsel mit ab. */
export function holidayMap(
  fromDate: LocalDate,
  toDate: LocalDate,
  land?: Bundesland,
): Map<LocalDate, string> {
  const map = new Map<LocalDate, string>();
  const firstYear = +fromDate.slice(0, 4);
  const lastYear = +toDate.slice(0, 4);

  for (let year = firstYear; year <= lastYear; year += 1) {
    for (const holiday of holidaysForYear(year, land)) {
      if (holiday.date >= fromDate && holiday.date <= toDate) {
        map.set(holiday.date, holiday.name);
      }
    }
  }
  return map;
}
